import { BaseGameLayer } from '../../engine/GameLayer';
import {
  CommandIssuer,
  CommandOutcome,
  FacilityTypeName,
  LOCAL_GOVERNOR,
  MayorVerb,
  Portal,
  TransferableEntity,
} from '@antification/protocol';
import {
  CarManager,
  CarManagerConfig,
  CellState,
  COLONY_COUNT,
  ConstructionManager,
  EconomyConfig,
  EconomyManager,
  EventBus,
  FacilityConfig,
  FoodSourceConfig,
  LabourConfig,
  MercyConfig,
  FiscalConfig,
  MarketConfig,
  FreightConfig,
  WelfareConfig,
  FoodSourceManager,
  GameEventTypes,
  DEFAULT_GOVERNOR_BRIEF,
  GovernorBrief,
  GovernorSeat,
  ResidentGovernor,
  HouseManager,
  HouseManagerConfig,
  MAX_ENTRANCES_PER_COLONY,
  type ParkingtonNewsItem,
  ParkingtonSurfaceAntController,
  PheromoneGrid,
  PheromoneGridConfig,
  PowerConfig,
  WaterConfig,
  SurfaceSim,
  SurfaceTraitParams,
  TerrainGrid,
} from '@antification/core';
import { Dimensions } from '../../engine/GameCanvas';
import { antSpriteCache } from '../formicarium/rendering/AntSpriteCache';

import {
  ParkingtonTool,
  type ToolDefinition,
  type SelectedEntity,
  type HoveredEntity,
  type ParkingtonState,
} from './ParkingtonTypes';

import { Camera } from './rendering/Camera';
import { EntityRenderer } from './rendering/EntityRenderer';
import { TerrainRenderer } from './rendering/TerrainRenderer';
import { CarRenderer } from './rendering/CarRenderer';
import { HouseRenderer } from './rendering/HouseRenderer';
import { FoodSourceRenderer } from './rendering/FoodSourceRenderer';
import { MapFeatureRenderer } from './rendering/MapFeatureRenderer';
import { MercyRenderer } from './rendering/MercyRenderer';
import { FreightRenderer } from './rendering/FreightRenderer';
import { HighlightRenderer } from './rendering/HighlightRenderer';
import { CanvasUIRenderer } from './rendering/CanvasUIRenderer';
import { HTMLUIRenderer } from './rendering/HTMLUIRenderer';
import { SeasonReportCard } from './rendering/SeasonReportCard';

import { SaveContext, SaveLoadSystem, SaveSlot, ViewSave } from './systems/SaveLoadSystem';
import { buildSeasonReport } from './systems/SeasonReport';
import { ConfigRegistry } from '../../engine/ConfigRegistry';
import type { SnapshotableColony } from '@antification/core';

import { MouseHandler, isPointInRect } from './input/MouseHandler';
import { KeyboardHandler } from './input/KeyboardHandler';
import { TouchZoomHandler } from './input/TouchZoomHandler';
import type { InputCallbacks } from './input/InputTypes';

/**
 * Parkington layer — the top-down parking lot construction game.
 *
 * The *client* half of the surface: a camera, a toolbar, save slots and a
 * renderer over a `SurfaceSim`. Everything that decides something lives in the
 * sim, which is why this file can touch `document`, `localStorage` and the wall
 * clock freely and the simulation still runs identically in a server with none
 * of them (ADR-003).
 */
export class ParkingtonLayer extends BaseGameLayer {
  readonly id = 'parkington';
  readonly displayName = 'Parkington';

  /** The deterministic simulation this layer draws and drives. */
  private sim: SurfaceSim;

  /**
   * How this client speaks to it.
   *
   * The layer never calls a manager to change something. It issues verbs, the
   * sim applies them at the head of a tick, and the layer finds out what
   * happened from the outcomes `update()` returns. That indirection is the
   * whole of ADR-004: it costs one frame of latency in single-player and it is
   * what makes a session a replayable record rather than a sequence of mouse
   * events that happened to a machine.
   */
  private readonly mayor: CommandIssuer;

  /**
   * The polity's issuer.
   *
   * A second issuer under a second actor, which is the first time this codebase
   * has had two. It needs its own because `(actor, seq)` is how the queue
   * recognises a retransmission: two issuers sharing an actor would collide and
   * one of them would have its commands discarded as duplicates.
   *
   * It used to be driven by nothing at all — a console handle for a governor that
   * did not exist. `seat` below is the governor, and this is how it speaks.
   */
  private readonly governor: CommandIssuer;

  /**
   * The governor in the chair over this district.
   *
   * `docs/roadmap.md`'s second Phase 3 item: the scripted polity that eleven ADRs
   * built mechanics for, promoted into the shipped client as a rule over the public
   * record (`docs/governor.md`). It reads the Chronicle at every settlement, sets
   * the tithe against what the polity's appetite did not get, seizes ground when
   * the money did not come, and routes freight where the mayor has built somewhere
   * for it to enter.
   *
   * Nothing on screen announces it, and that is not an omission: §12's
   * Correspondence tab and Ledger Dock are Phase 4's, so a mayor finds out about a
   * levy the way they find out about a pothole — from the ticker line
   * `SurfaceSim.setTithe` writes, and from the Season Report's fiscal block. The
   * budget panel's tithe help text has said *"Not your number"* since ADR-021 and
   * now it is somebody's.
   *
   * Turn it off from the console (`parkingtonDebug.governor.resident.off()`) to
   * play the district that shipped before this session.
   */
  private readonly seat: GovernorSeat;

  /**
   * The policy in that chair, held by its own type so the brief can be edited.
   *
   * `GovernorSeat` takes a `GovernorPolicy`, which is the interface an LLM loop
   * would implement (roadmap item 2's other half). A brief belongs to *this*
   * implementation, so the client keeps the concrete reference rather than asking
   * the seat what kind of governor it is holding.
   */
  private readonly resident = new ResidentGovernor(DEFAULT_GOVERNOR_BRIEF);

  // Manager accessors. The sim owns these; the renderers and input handlers
  // below have always addressed them by these names.
  private get grid(): TerrainGrid { return this.sim.grid; }
  private get construction(): ConstructionManager { return this.sim.construction; }
  private get cars(): CarManager { return this.sim.cars; }
  private get houses(): HouseManager { return this.sim.houses; }
  private get economy(): EconomyManager { return this.sim.economy; }
  private get foodSources(): FoodSourceManager { return this.sim.foodSources; }
  private get pheromones(): PheromoneGrid { return this.sim.pheromones; }
  private get surfaceAntController(): ParkingtonSurfaceAntController { return this.sim.surfaceAnts; }
  private get portalCells(): Array<{ x: number; y: number }> { return this.sim.portalCells; }

  /** Which entrances a facility's foundation has closed. Re-read every frame. */
  private get sealedPortals(): boolean[] {
    return this.sim.portalCells.map((_, i) => this.sim.isEntranceSealed(i));
  }

  /**
   * Where this client is looking.
   *
   * Pan, zoom and the isometric projection. It lived in `TerrainGrid` until
   * ADR-009, which meant the ground knew about pixels and a headless district
   * carried a camera nobody was looking through. Nothing in `this.sim` can see
   * this object, and that is the whole of the arrangement.
   */
  private readonly camera = new Camera();

  // --- Extracted modules ---
  private terrainRenderer = new TerrainRenderer();
  private carRenderer = new CarRenderer();
  private houseRenderer = new HouseRenderer();
  private entityRenderer = new EntityRenderer(
    this.terrainRenderer,
    this.carRenderer,
    this.houseRenderer
  );
  private foodSourceRenderer = new FoodSourceRenderer();
  private mapFeatureRenderer = new MapFeatureRenderer();
  private mercyRenderer = new MercyRenderer();
  private freightRenderer = new FreightRenderer();
  private highlightRenderer = new HighlightRenderer();
  private canvasUIRenderer = new CanvasUIRenderer();
  private htmlUIRenderer = new HTMLUIRenderer();
  private seasonReportCard = new SeasonReportCard();
  private saveLoadSystem!: SaveLoadSystem;
  private mouseHandler = new MouseHandler();
  private keyboardHandler = new KeyboardHandler();
  private touchZoomHandler = new TouchZoomHandler();

  // --- Debug flags ---
  private showPheromones = false;
  private skipVegetation = false;
  private skipHouses = false;
  private skipCars = false;
  private skipAnts = false;
  private skipTerrain = false;
  private skipFoodSources = false;

  // --- Portal state ---
  private portalPulse = 0;
  static readonly COLONY_COUNT = COLONY_COUNT;
  static readonly MAX_ENTRANCES_PER_COLONY = MAX_ENTRANCES_PER_COLONY;

  // --- Input state ---
  private mouseX = 0;
  private mouseY = 0;
  private hoveredCell: { x: number; y: number } | null = null;
  private isMouseDown = false;
  private inputBlocked = false;

  // --- Tool state ---
  private currentTool: ParkingtonTool = ParkingtonTool.HIGHWAY;
  private toolDefinitions: ToolDefinition[] = [
    { id: ParkingtonTool.SELECT, name: 'Select', icon: '⬚', description: 'Select cars and buildings', available: true, shortcut: 'S' },
    { id: ParkingtonTool.PRECISION, name: 'Precision', icon: '·', description: '1x1 precise development', available: false, shortcut: 'P' },
    { id: ParkingtonTool.HIGHWAY, name: 'Highway', icon: '▣', description: '3x3 road builder', available: true, shortcut: 'H' },
    // §10's mercy builds, described in the register the rest of the row uses: what
    // the button does to the ground, and not one word about why. The prices are
    // named because a price is a fact; nothing here says what any of it is for.
    { id: ParkingtonTool.COVENANT, name: 'Covenant', icon: '⌇', description: 'Deed a cell wild, permanently', available: true, shortcut: 'C' },
    { id: ParkingtonTool.HEDGEROW, name: 'Hedgerow', icon: '❦', description: 'Plant a bush on open ground', available: true, shortcut: 'B' },
    { id: ParkingtonTool.UNDERCROSS, name: 'Undercrossing', icon: '⌒', description: 'A way under a paved cell', available: true, shortcut: 'U' },
    // §5's interchange, in the same register: what it does to the ground, and not
    // one word about the trucks. A mayor who wants to know what a corridor brings
    // finds out the way the district finds out everything — by having one.
    { id: ParkingtonTool.INTERCHANGE, name: 'Interchange', icon: '⇹', description: 'Where national freight enters the map', available: true, shortcut: 'I' },
    { id: ParkingtonTool.DEMOLISH, name: 'Demolish', icon: '✕', description: 'Remove structures', available: false, shortcut: 'X' },
    { id: ParkingtonTool.ZONE, name: 'Zone', icon: '▦', description: 'Designate zones', available: false, shortcut: 'Z' },
  ];

  // --- Selection state ---
  private selectedEntity: SelectedEntity | null = null;
  private hoveredEntity: HoveredEntity | null = null;
  private selectionPaneCloseArea: { x: number; y: number; w: number; h: number } | null = null;

  // --- Car tracking ---
  private trackedCarId: number | null = null;
  private mouseIdleTimer = 0;
  private lastMouseX = 0;
  private lastMouseY = 0;
  private readonly MOUSE_IDLE_THRESHOLD = 300;

  // --- Brush state ---
  private cellsChanged = 0;
  private readonly PRECISION_UNLOCK = 600;

  /** Mercy cells committed since the last time anybody was told. */
  private mercyCellsPending = 0;
  private mercyReportTimer = 0;
  /** Sim-ms between "this is what it has cost" lines. See `reportMercySpending`. */
  private readonly MERCY_REPORT_INTERVAL = 900;
  private currentBrushSize = 3;
  private precisionUnlocked = false;

  // --- Save/Load ---
  private showSaveSlots = false;
  private readonly AUTOSAVE_INTERVAL = 30000;
  private autosaveTimer = 0;

  // --- The season ---
  /**
   * How many Chronicle lines this client has already seen.
   *
   * The client *observes* the settlement rather than being told about it: the
   * simulation emits no season event, and the card is raised because the record
   * got longer under a watcher who was there. That is the smallest possible
   * version of §4's promise — witnessing does not change the totals — since the
   * only thing on this path that reads a season is a length comparison, and
   * deleting the card would leave the district running exactly as it does.
   *
   * A load moves the Chronicle to somewhere this counter has no relationship
   * with, so `loadGame` resets it. Otherwise a restored district would either
   * pop a card for a season the player never watched close, or go quiet until it
   * had caught up with a world it had left.
   */
  private seasonsSeen = 0;

  /**
   * The colonies beneath this surface, for the save path only.
   *
   * A save is a whole district (ADR-006), and this layer is only its top half.
   * Supplied by `Game` as a getter over structural interfaces, so the surface
   * still knows nothing about the Formicarium beyond that it can be snapshotted.
   */
  private getColonies: () => readonly SnapshotableColony[] = () => [];

  // --- Pan mode ---
  private isPanMode = false;
  private panTouchStart: { x: number; y: number } | null = null;

  // --- Colony selection ---
  private selectedColonyIndex = 0;

  constructor(events: EventBus, worldSeed = 'parkington') {
    super(events);
    this.sim = new SurfaceSim(events, worldSeed, { layerId: this.id });
    // No network, so no reason to aim past the next tick. When there is a
    // server, `InputDelayEstimator.delayTicks` sets this.
    this.mayor = new CommandIssuer(this.sim);
    this.governor = new CommandIssuer(this.sim, LOCAL_GOVERNOR);
    // The polity, under the brief the client ships with. Attaching it changes
    // nothing at all until the first season closes — see `DEFAULT_GOVERNOR_BRIEF`,
    // which is written so that this is true and asserted by hash in
    // `governor.test.ts`.
    this.seat = new GovernorSeat(this.sim, this.governor, this.resident);

    // Storage, prompts and notifications. Everything the save *is* comes from
    // the sim's snapshot, so this needs no manager references at all.
    this.saveLoadSystem = new SaveLoadSystem(this.events);

    // Register event types
    this.events.registerEventTypes('parkington', [
      GameEventTypes.CONSTRUCTION_PHASE_CHANGED,
      GameEventTypes.CELL_STATE_CHANGED,
      GameEventTypes.GRADING_VIBRATION,
      GameEventTypes.EXCAVATION_STARTED,
      GameEventTypes.CONCRETE_POURED,
      GameEventTypes.PARKING_LOT_COMPLETE,
    ]);

    // Listen for colony selection
    this.events.on(GameEventTypes.COLONY_SELECTED, (payload) => {
      const data = payload as { colonyIndex: number };
      this.selectedColonyIndex = data.colonyIndex;
      this.selectedEntity = { type: 'portal', id: data.colonyIndex };
    });
  }

  // --- ParkingtonState implementation (readonly view for modules) ---
  private get stateView(): ParkingtonState {
    return {
      width: this.width,
      height: this.height,
      mouseX: this.mouseX,
      mouseY: this.mouseY,
      hoveredCell: this.hoveredCell,
      isMouseDown: this.isMouseDown,
      inputBlocked: this.inputBlocked,
      currentTool: this.currentTool,
      toolDefinitions: this.toolDefinitions,
      selectedEntity: this.selectedEntity,
      hoveredEntity: this.hoveredEntity,
      trackedCarId: this.trackedCarId,
      cellsChanged: this.cellsChanged,
      precisionUnlocked: this.precisionUnlocked,
      currentBrushSize: this.currentBrushSize,
      showPheromones: this.showPheromones,
      showSaveSlots: this.showSaveSlots,
      portalPulse: this.portalPulse,
      portalCells: this.portalCells,
      sealedPortals: this.sealedPortals,
      selectedColonyIndex: this.selectedColonyIndex,
      isPanMode: this.isPanMode,
      panTouchStart: this.panTouchStart,
      autosaveTimer: this.autosaveTimer,
      selectionPaneCloseArea: this.selectionPaneCloseArea,
      skipVegetation: this.skipVegetation,
      skipHouses: this.skipHouses,
      skipCars: this.skipCars,
      skipAnts: this.skipAnts,
      skipTerrain: this.skipTerrain,
      skipFoodSources: this.skipFoodSources,
      isActivelyTransforming: this.touchZoomHandler.isTransforming,
      camera: this.camera,
      grid: this.grid,
      construction: this.construction,
      cars: this.cars,
      houses: this.houses,
      foodSources: this.foodSources,
      pheromones: this.pheromones,
      surfaceAntController: this.surfaceAntController,
      economy: this.economy,
      power: this.sim.power,
      water: this.sim.water,
      facilities: this.sim.facilities,
      labour: this.sim.labour,
      welfare: this.sim.welfare,
      mercy: this.sim.mercy,
      freight: this.sim.freight,
      fiscal: this.sim.fiscal,
      season: this.sim.getSeason(),
      seasonReportShown: this.seasonReportCard.isVisible(),
      portals: this.sim.portals,
      events: this.events,
      PRECISION_UNLOCK: this.PRECISION_UNLOCK,
      AUTOSAVE_INTERVAL: this.AUTOSAVE_INTERVAL,
      COLONY_COUNT: COLONY_COUNT,
    };
  }

  // --- Input callbacks implementation ---
  private get inputCallbacks(): InputCallbacks {
    return {
      setMousePosition: (x, y) => { this.mouseX = x; this.mouseY = y; },
      setLastMousePosition: (x, y) => { this.lastMouseX = x; this.lastMouseY = y; },
      resetMouseIdleTimer: () => { this.mouseIdleTimer = 0; },
      setHoveredCell: (cell) => { this.hoveredCell = cell; },
      setHoveredEntity: (entity) => { this.hoveredEntity = entity; },
      setSelectedEntity: (entity) => { this.selectedEntity = entity; },
      setIsMouseDown: (down) => { this.isMouseDown = down; },
      setTool: (tool) => { this.setTool(tool); },
      togglePanMode: () => { this.togglePanMode(); },
      setPanTouchStart: (pos) => { this.panTouchStart = pos; },
      applyBrush: (x, y) => { this.applyBrush(x, y); },
      updateHoveredEntity: () => { this.updateHoveredEntity(); },
      emitColonySelectRequest: (colonyIndex) => {
        this.events.emit(GameEventTypes.COLONY_SELECT_REQUEST, { colonyIndex });
      },
      toggleShowPheromones: () => {
        this.showPheromones = !this.showPheromones;
        this.events.emit(GameEventTypes.NOTIFICATION_SHOW, {
          message: this.showPheromones ? 'Pheromone trails visible (T)' : 'Pheromone trails hidden (T)',
          type: 'info',
        });
      },
      toggleStaffing: (x, y) => { this.toggleStaffing(x, y); },
      toggleSeasonReport: () => { this.toggleSeasonReport(); },
      loadGame: (slotIndex) => { this.loadGame(slotIndex); },
      markTransforming: () => { this.touchZoomHandler.markTransforming(this.terrainRenderer); },
      screenToCell: (x, y) => this.screenToCell(x, y),
      triggerSnapBack: () => { this.touchZoomHandler.triggerSnapBack(this.stateView); },
    };
  }

  async init(): Promise<void> {
    // Set up mouse tracking
    const topCanvas = document.getElementById('parking-canvas');
    if (topCanvas) {
      topCanvas.addEventListener('mousemove', (e) => this.mouseHandler.handleMouseMove(e, this.stateView, this.inputCallbacks));
      topCanvas.addEventListener('mousedown', (e) => this.onMouseDown(e));
      topCanvas.addEventListener('mouseup', (e) => this.mouseHandler.handleMouseUp(e, this.stateView, this.inputCallbacks));
      topCanvas.addEventListener('mouseleave', (e) => this.mouseHandler.handleMouseUp(e, this.stateView, this.inputCallbacks));

      topCanvas.addEventListener('wheel', (e) => this.touchZoomHandler.handleWheel(e, this.stateView, this.inputCallbacks), { passive: false });

      topCanvas.addEventListener('touchstart', (e) => this.touchZoomHandler.handleTouchStart(e, this.stateView, this.inputCallbacks), { passive: false });
      topCanvas.addEventListener('touchmove', (e) => this.touchZoomHandler.handleTouchMove(e, this.stateView, this.inputCallbacks), { passive: false });
      topCanvas.addEventListener('touchend', (e) => this.touchZoomHandler.handleTouchEnd(e, this.stateView, this.inputCallbacks));
      topCanvas.addEventListener('touchcancel', (e) => this.touchZoomHandler.handleTouchEnd(e, this.stateView, this.inputCallbacks));
    }

    document.addEventListener('keydown', (e) => this.keyboardHandler.handleKeyDown(e, this.stateView, this.inputCallbacks));
    document.addEventListener('keyup', (e) => this.keyboardHandler.handleKeyUp(e, this.stateView, this.inputCallbacks));

    // Set up portals
    this.sim.setupPortals();

    // Expose performance debug toggles
    (window as any).parkingtonDebug = {
      skipVegetation: (skip: boolean) => { this.skipVegetation = skip; },
      skipHouses: (skip: boolean) => { this.skipHouses = skip; },
      skipCars: (skip: boolean) => { this.skipCars = skip; },
      skipAnts: (skip: boolean) => { this.skipAnts = skip; },
      skipTerrain: (skip: boolean) => { this.skipTerrain = skip; },
      skipFoodSources: (skip: boolean) => { this.skipFoodSources = skip; },
      skipAll: (skip: boolean) => {
        this.skipVegetation = skip;
        this.skipHouses = skip;
        this.skipCars = skip;
        this.skipAnts = skip;
        this.skipFoodSources = skip;
      },
      getStats: () => (window as any).parkingtonRenderStats,
      invalidateTerrainCache: () => { this.terrainRenderer.invalidate(); },
      // The simulation itself, for inspecting state from the console or from a
      // browser smoke test. Read-only by convention: writing to it from here
      // would be exactly the kind of ambient input the sim exists to refuse.
      getSim: () => this.sim,
      /**
       * The polity, by hand — and, since this session, the one that plays itself.
       *
       * There is no *interface* for any of this and there should not be one: §6's
       * facilities reach a district through the Siting Protocol, and a mayor's
       * side of that protocol is City Hall, which is Phase 4. So a governor's own
       * paperwork lives where `sim.setRainfall(0.4)` lives — on a console handle,
       * legible to the author and invisible to a player.
       *
       * The verbs go through a `CommandIssuer` rather than calling the sim, because
       * a facility is a *command* (see `SurfaceSim.facilities`) and this is the only
       * channel that stamps one with a tick.
       */
      governor: {
        /**
         * The resident governor: its brief, its minutes, and its off switch.
         *
         * `minutes()` is the whole of what the polity has done, season by season,
         * in the register a governor's own file would use — `docs/governor.md`
         * explains each move kind. `brief()` is what it is playing under, and
         * `set()` edits it live (the same fields the settings panel's *The Polity*
         * section edits, for anyone who would rather type).
         *
         * `off()` empties the chair. The district then behaves exactly as it did
         * before this session, which is the only honest way to compare a governed
         * district with an ungoverned one: ADR-024 D161 measured that the
         * counterfactual *inside* one district is unavailable, so the comparison has
         * to be two runs from the same seed.
         */
        resident: {
          brief: () => this.resident.getBrief(),
          minutes: () => this.seat.getMinutes(),
          set: (partial: Partial<GovernorBrief>) => this.updatePolityBrief(partial),
          off: () => this.seat.setActive(false),
          on: () => this.seat.setActive(true),
          active: () => this.seat.isActive(),
        },
        forcePlace: (x: number, y: number) =>
          this.governor.forcePlace(FacilityTypeName.DATA_CENTER, x, y),
        estate: () => this.sim.facilities.getFacilities(),
        /**
         * What is still being built, and how far the lorries have got.
         *
         * A separate handle from `estate()` because it answers a different
         * question and stops answering it: a facility that has opened is not
         * here, and there is nothing to look at in a district whose estate is
         * finished. `hoard` goes back to zero every season a season of progress
         * is banked, which is what §7's *"the footprint hoards"* means.
         */
        building: () =>
          this.sim.facilities
            .getFacilities()
            .filter((f) => f.openedTick === null)
            .map((f) => ({
              id: f.id,
              at: `${f.x},${f.y}`,
              needs: this.sim.facilities.goodsPerSeasonFor(f.type),
              seasons: this.sim.facilities.buildSeasonsFor(f.type),
              ...this.sim.facilities.getBuild(f.id),
            })),
        /**
         * Who works there, by name.
         *
         * A console handle rather than a screen, because it is not a mayor's
         * question in this build: the panel says how many of your families work
         * at the estate and the tooltip says the same, and *which* families is a
         * fact §12's City Hall will present properly. Here it is a debugging aid
         * with a nice side effect — the eight names are the eight families whose
         * commute is on your roads.
         */
        roster: () => this.sim.facilities.getFacilities().map((f) => ({
          facility: f.id,
          staff: this.sim.labour.rosterOf(f.id).map(
            (id) => this.sim.houses.getFamily(id)?.name ?? `#${id}`
          ),
          withdrawn: this.sim.labour.isWithdrawn(f.id),
        })),
        /**
         * How much national freight crosses this district, in truck-legs per
         * budget cycle.
         *
         * A method call rather than a verb, unlike `forcePlace` beside it and like
         * `setRainfall` and `setPowerAllocation` — a dial re-decided from scratch
         * every cycle can be a scalar, because a district restored from a snapshot
         * re-derives what crosses it. See `FreightManager.setOffered`.
         *
         * It does nothing at all until the mayor has built two interchanges. A
         * polity that routes freight at a district with nowhere for it to enter
         * gets a lapsed-leg count and no trucks, which is the honest record of what
         * happened.
         */
        setFreight: (legsPerCycle: number | null) => this.sim.setFreight(legsPerCycle),
        corridor: () => ({
          ...this.sim.freight.getLedger(),
          // After the ledger, which has an `interchanges` count of its own: the
          // structures themselves are what a console is actually for.
          interchanges: this.sim.freight.getInterchanges(),
          offered: this.sim.freight.getOffered(),
        }),
        /**
         * Post a world price to this district.
         *
         * A method call rather than a verb, for `setFreight`'s reason and
         * `setRainfall`'s: a price is re-read every cycle, so a district restored
         * from a snapshot trades at what it was quoted rather than at what a
         * command log replays. Both fields are optional and an omitted one is left
         * alone. See `docs/world-market.md`.
         *
         * The bond yield reaches the treasury immediately — it is what the district
         * borrows at from the next accrual, not from the next season.
         */
        postMarket: (prices: { computePrice?: number; bondYield?: number }) =>
          this.sim.postMarket(prices),
        /**
         * What the world has paid this district's estate, and what it charges.
         *
         * Most of the money in here is **not the district's**: `salesRevenue` is
         * the polity's, and a mayor reading this handle is reading somebody else's
         * accounts. That is exactly why it is a console handle and not a panel.
         */
        market: () => ({
          ...this.sim.market.getLedger(),
          computePrice: this.sim.market.getComputePrice(),
          bondYield: this.sim.market.getBondYield(),
          operatingProfit: this.sim.market.getOperatingProfit(),
          seasonQuote: this.sim.market.quoteSeason(this.sim.facilities.getComputeCapacity()),
        }),
      },
    };
    console.log('[Parkington] Debug: skipAll(true), skipFoodSources(true), skipTerrain(true)');

    // Debug terrain
    if (window.location.search.includes('debug')) {
      this.generateDebugTerrain();
    }

    // Initialize HTML UI
    this.htmlUIRenderer.initHTMLUI(this.stateView, {
      setTool: (tool) => this.setTool(tool),
      togglePanMode: () => this.togglePanMode(),
      saveGame: () => { this.saveGame(); this.showSaveSlots = false; },
      toggleSaveSlots: () => { this.toggleSaveSlots(); },
      hasSavedGame: () => this.hasSavedGame(),
    });

    this.seasonReportCard.init();
  }

  // --- Mouse down handler (needs save slot area access from canvas UI) ---
  private onMouseDown(e: MouseEvent): void {
    if (this.inputBlocked) return;

    // Check close button
    if (this.selectionPaneCloseArea && isPointInRect(this.mouseX, this.mouseY, this.selectionPaneCloseArea)) {
      this.selectedEntity = null;
      return;
    }

    // Check save slot clicks
    if (this.showSaveSlots) {
      for (let i = 0; i < this.canvasUIRenderer.saveSlotAreas.length; i++) {
        if (isPointInRect(this.mouseX, this.mouseY, this.canvasUIRenderer.saveSlotAreas[i])) {
          this.loadGame(i);
          return;
        }
      }
      this.showSaveSlots = false;
      return;
    }

    // Delegate rest to mouse handler
    this.mouseHandler.handleMouseDown(e, this.stateView, this.inputCallbacks);
  }

  // --- Tool management ---
  setTool(tool: ParkingtonTool): void {
    if (this.currentTool === tool) return;
    this.currentTool = tool;

    if (tool !== ParkingtonTool.SELECT) {
      this.selectedEntity = null;
      this.hoveredEntity = null;
      this.trackedCarId = null;
    }

    if (this.isPanMode) {
      this.isPanMode = false;
      this.htmlUIRenderer.updatePanButtonState(false);
    }

    this.htmlUIRenderer.updateToolButtonStates(this.stateView);
  }

  private togglePanMode(): void {
    this.isPanMode = !this.isPanMode;
    this.panTouchStart = null;
    this.htmlUIRenderer.updatePanButtonState(this.isPanMode);
  }

  // --- Hovered entity detection ---
  private updateHoveredEntity(): void {
    if (!this.hoveredCell) {
      this.hoveredEntity = null;
      return;
    }

    // Portal. A sealed one is not here to be found: the mouse falls through to
    // whatever the slab is standing on, which is the ground.
    for (let i = 0; i < this.portalCells.length; i++) {
      const portal = this.portalCells[i];
      if (portal.x === this.hoveredCell.x && portal.y === this.hoveredCell.y) {
        if (this.sim.isEntranceSealed(i)) break;
        this.hoveredEntity = { type: 'portal', id: i };
        return;
      }
    }

    // Car
    const carId = this.cars.getCarIdAt(this.hoveredCell.x, this.hoveredCell.y);
    if (carId !== null) {
      this.hoveredEntity = { type: 'car', id: carId };
      return;
    }

    // Building
    const housesWithDepth = this.houses.getHousesWithDepth();
    for (const { house } of housesWithDepth) {
      for (const cell of house.footprint) {
        if (house.gridX + cell.dx === this.hoveredCell.x && house.gridY + cell.dy === this.hoveredCell.y) {
          this.hoveredEntity = { type: 'building', id: house.id };
          return;
        }
      }
    }

    this.hoveredEntity = null;
  }

  // --- Brush ---
  private getBrushSize(): number {
    if (this.currentTool === ParkingtonTool.PRECISION) return 1;
    // Every mercy build is one cell at a time, and the reason is the price rather
    // than the geometry: a 3×3 covenant is nine cells and $2,700 per click, which is
    // a sum nobody would mean. A mayor who wants a hedge drags.
    if (this.currentTool === ParkingtonTool.COVENANT) return 1;
    if (this.currentTool === ParkingtonTool.HEDGEROW) return 1;
    if (this.currentTool === ParkingtonTool.UNDERCROSS) return 1;
    // One click, one structure. The second cell is the simulation's business.
    if (this.currentTool === ParkingtonTool.INTERCHANGE) return 1;
    return 3;
  }

  private updatePrecisionUnlock(): void {
    if (!this.precisionUnlocked && this.cellsChanged >= this.PRECISION_UNLOCK) {
      this.precisionUnlocked = true;
      const precisionTool = this.toolDefinitions.find(t => t.id === ParkingtonTool.PRECISION);
      if (precisionTool) {
        precisionTool.available = true;
      }
      this.events.emit(GameEventTypes.NOTIFICATION_SHOW, {
        message: 'Precision tool unlocked! Press P for precise 1x1 cuts.',
        type: 'success',
      });
    }
  }

  /**
   * Turn a brush application into verbs.
   *
   * One `PAVE` per cell the brush covers (ADR-004). Cells off the edge of the
   * map are rejected by the sim and never enter the log, which is the same
   * outcome the old direct call had and a cheaper way to get it.
   *
   * Nothing is applied here. The commands land at the head of the next tick,
   * and `recordConstruction` learns from the outcomes what actually changed —
   * a cell already curing swallows a `PAVE` without advancing, and the count
   * that unlocks the Precision tool must not be told otherwise.
   */
  private applyBrush(centerX: number, centerY: number): void {
    const brushSize = this.getBrushSize();
    const radius = Math.floor(brushSize / 2);

    for (let dy = -radius; dy <= radius; dy++) {
      for (let dx = -radius; dx <= radius; dx++) {
        if (brushSize === 1 || dx * dx + dy * dy <= radius * radius + 1) {
          this.issueBrushVerb(centerX + dx, centerY + dy);
        }
      }
    }
  }

  /**
   * Which verb the tool under the cursor means.
   *
   * The dispatch is here rather than in the input handler for the reason ADR-004
   * gave for `applyBrush` itself: a handler names a cell and this object turns it
   * into a sentence the simulation can replay. Four of the six brush tools produce
   * a different sentence about the same cell, and the sim decides at the head of a
   * tick which of them meant anything.
   */
  private issueBrushVerb(x: number, y: number): void {
    switch (this.currentTool) {
      case ParkingtonTool.COVENANT:
        this.mayor.covenant(x, y);
        return;
      case ParkingtonTool.HEDGEROW:
        this.mayor.hedgerow(x, y);
        return;
      case ParkingtonTool.UNDERCROSS:
        this.mayor.undercross(x, y);
        return;
      case ParkingtonTool.INTERCHANGE:
        this.mayor.interchange(x, y, this.interchangeAxis(x, y));
        return;
      default:
        this.mayor.pave(x, y);
    }
  }

  /**
   * Call out, or send back, the staff of the facility under a cell.
   *
   * §10's `withdraw_staffing(facility_id)` / `restore_staffing`, aimed with the
   * cursor. The keyboard names a cell because a mayor has no other way to name a
   * facility yet — City Hall (§12, Phase 4) is where a list of them lives.
   *
   * Nothing is applied here. The verb lands at the head of the next tick like the
   * pavement does, and the notification is worded as a thing the *district* did:
   * calling your own families out is the one move in this game the mayor makes
   * against somebody above them, and it should read like one.
   */
  /**
   * Which way the ramp lies, given the road under the cursor.
   *
   * A client's decision, and the reason `INTERCHANGE` spells the axis rather than
   * inferring it (`@antification/protocol`): the log records what was built, and a
   * client that guesses better later changes no verb and re-records no season.
   *
   * East if the cell to the east is road, otherwise south. So an interchange
   * dropped on a horizontal highway lies along it, one dropped on a vertical one
   * lies along that, and one dropped on a single stray cell of pavement is refused
   * by the simulation because its second cell is grass.
   */
  private interchangeAxis(x: number, y: number): 'h' | 'v' {
    const east = this.sim.grid.getCell(x + 1, y)?.state;
    const paved = east === CellState.POURED
      || east === CellState.CURED
      || east === CellState.PAINTED;
    return paved ? 'h' : 'v';
  }

  private toggleStaffing(x: number, y: number): void {
    const facilityId = this.sim.facilities.covers(x, y);
    if (facilityId === null) return;

    if (this.sim.labour.isWithdrawn(facilityId)) {
      this.mayor.restoreStaffing();
      this.events.emit(GameEventTypes.NOTIFICATION_SHOW, {
        message: 'Staff returning to the federal facility',
        type: 'info',
      });
      return;
    }

    this.mayor.withdrawStaffing(facilityId);
    const staff = this.sim.labour.rosterOf(facilityId).length;
    this.events.emit(GameEventTypes.NOTIFICATION_SHOW, {
      message: `${staff} families withdrawn from the federal facility (W)`,
      type: 'info',
    });
  }

  /** Count the ground actually broken by the tick's commands. */
  private recordConstruction(outcomes: readonly CommandOutcome[]): void {
    let changed = false;
    let mercy = 0;
    for (const outcome of outcomes) {
      if (!outcome.applied) continue;
      if (outcome.command.verb === MayorVerb.PAVE) {
        this.cellsChanged++;
        changed = true;
      } else if (
        outcome.command.verb === MayorVerb.COVENANT
        || outcome.command.verb === MayorVerb.HEDGEROW
        || outcome.command.verb === MayorVerb.UNDERCROSS
      ) {
        // Deliberately not counted into `cellsChanged`. That number unlocks the
        // Precision tool and is the game's own measure of how much a mayor has
        // developed; a covenant is the opposite of developing something, and a
        // hedgerow that unlocked a finer brush would be the interface paying for
        // mercy in the one currency it has.
        mercy++;
      }
    }
    if (changed) {
      this.updatePrecisionUnlock();
    }
    if (mercy > 0) {
      this.mercyCellsPending += mercy;
    }
  }

  /**
   * Say what the works have cost, occasionally.
   *
   * Throttled the way `ConstructionManager.maybeNotify` is throttled and for the
   * same reason: these are brush tools, one cell lands per frame while the button is
   * down, and a toast per cell would bury the number in itself. So the cells
   * accumulate and the notification names the total spent so far — a figure, in the
   * flat voice the district uses about its own budget, with nothing in it about what
   * was bought or why.
   *
   * `spent` comes from the simulation rather than from a price list here. The client
   * does not know what a covenant costs and should not: the price is a world
   * parameter a Charter will eventually set (§17), and a toolbar that had its own
   * copy would be a toolbar that could disagree with the ledger.
   */
  private reportMercySpending(dt: number): void {
    if (this.mercyCellsPending === 0) return;
    this.mercyReportTimer += dt;
    if (this.mercyReportTimer < this.MERCY_REPORT_INTERVAL) return;

    const cells = this.mercyCellsPending;
    const spent = Math.round(this.sim.mercy.getLedger().spent);
    this.mercyCellsPending = 0;
    this.mercyReportTimer = 0;

    this.events.emit(GameEventTypes.NOTIFICATION_SHOW, {
      message: `${cells} ${cells === 1 ? 'cell' : 'cells'} committed — `
        + `$${spent.toLocaleString('en-US')} spent on works to date`,
      type: 'info',
    });
  }

  // --- The season ---

  /**
   * Raise a card when the Chronicle gets longer.
   *
   * §12's *Rhythm*: **at each settlement, one card**. There is no season event on
   * the bus and none is wanted — the simulation's job at a boundary is to write a
   * record, and a client that had to be *told* about it would be a client whose
   * absence the simulation could notice.
   *
   * So this is a length comparison, run once a tick, and it is the entire
   * mechanism. `> seasonsSeen` rather than `!==` so that a Chronicle which moved
   * for any other reason — a load, a restore from file — resyncs silently instead
   * of announcing somebody else's history as news.
   */
  private watchForSettlement(): void {
    const chronicle = this.sim.getChronicle();
    if (chronicle.length <= this.seasonsSeen) {
      this.seasonsSeen = chronicle.length;
      return;
    }
    this.seasonsSeen = chronicle.length;
    this.showLatestReport();
  }

  /** Put the last closed season on screen, or take it off. The **R** key. */
  private toggleSeasonReport(): void {
    if (this.seasonReportCard.isVisible()) {
      this.seasonReportCard.hide();
      return;
    }
    this.showLatestReport();
  }

  /**
   * Build the card from the Chronicle's last two lines.
   *
   * Two lines and nothing else: the report is a pure function of the public
   * record (`systems/SeasonReport.ts`), so this method holds no state, reads no
   * manager, and cannot put a figure on screen that the record does not carry.
   *
   * A district in its first season has one line and no predecessor, which
   * `buildSeasonReport` handles; a district with none does nothing at all,
   * silently, which is the same answer the toolbar gives for a tool that is not
   * unlocked yet.
   */
  private showLatestReport(): void {
    const chronicle = this.sim.getChronicle();
    if (chronicle.length === 0) return;
    const record = chronicle[chronicle.length - 1];
    const previous = chronicle.length > 1 ? chronicle[chronicle.length - 2] : undefined;
    this.seasonReportCard.show(buildSeasonReport(record, previous));
  }

  // --- Save/Load forwarding ---
  saveGame(isAutosave = false): void {
    this.saveLoadSystem.saveGame(isAutosave, this.saveContext);
  }

  /**
   * The district, the toolbar, and the parameters it was played under.
   *
   * Everything the simulation is comes from `sim.snapshot()` and the colonies'.
   * What is left here is genuinely the client's: an unlock the player earned, a
   * brush, and the settings panel's values. Portal cells, timers, pheromones and
   * curing concrete used to be assembled by hand at this seam, and losing one of
   * them was a silent bug rather than a compile error.
   */
  private get saveContext(): SaveContext {
    return {
      district: {
        seed: this.sim.seed,
        surface: this.sim,
        colonies: this.getColonies(),
      },
      getView: (): ViewSave => ({
        precisionUnlocked: this.precisionUnlocked,
        cellsChanged: this.cellsChanged,
        currentBrushSize: this.currentBrushSize,
      }),
      applyView: (view: ViewSave) => {
        this.precisionUnlocked = view.precisionUnlocked;
        this.cellsChanged = view.cellsChanged;
        this.currentBrushSize = view.currentBrushSize;

        const precisionTool = this.toolDefinitions.find(t => t.id === ParkingtonTool.PRECISION);
        if (precisionTool) {
          precisionTool.available = view.precisionUnlocked;
        }
        // A save from before the unlock takes the tool back, so don't leave the
        // player holding one the toolbar no longer offers.
        if (!view.precisionUnlocked && this.currentTool === ParkingtonTool.PRECISION) {
          this.setTool(ParkingtonTool.HIGHWAY);
        }
        this.htmlUIRenderer.updateToolButtonStates(this.stateView);
      },
      getSettings: () => ConfigRegistry.getInstance().exportSettings(),
      applySettings: (settings) => {
        ConfigRegistry.getInstance().importSettings(settings);
      },
    };
  }

  loadGame(slotIndex = 0): void {
    this.saveLoadSystem.loadGame(slotIndex, this.saveContext);
    this.showSaveSlots = false;
    // The restored lot carries the terrain version it was saved with, so the
    // counter alone cannot tell the renderer its cached image is of a different
    // district. Say so (ADR-008 D36).
    this.terrainRenderer.invalidate();
    // The Chronicle is now somebody else's, and so is the card on screen. See
    // `seasonsSeen`: a load is not a settlement, and none of what it restored
    // happened while this player was watching.
    this.seasonsSeen = this.sim.getChronicle().length;
    // Nor was any of it decided by the governor now in the chair. Every consequence
    // of the decisions that *were* taken came back with the snapshot — the rate in
    // force, the slabs, the legs being hauled — so the seat adopts the history
    // without acting on it, and its next move is at the next boundary.
    this.seat.resync();
    this.seasonReportCard.hide();
  }

  getSaveSlots(): SaveSlot[] {
    return this.saveLoadSystem.getSaveSlots();
  }

  hasSavedGame(): boolean {
    return this.saveLoadSystem.hasSavedGame();
  }

  toggleSaveSlots(): void {
    this.showSaveSlots = !this.showSaveSlots;
  }

  // --- Portal forwarding ---
  receiveEntity(entity: TransferableEntity, fromPortal: string): boolean {
    return this.sim.receiveEntity(entity, fromPortal);
  }

  /** Portals belong to the simulation; the layer only draws them. */
  getPortals(): Portal[] {
    return this.sim.portals;
  }

  // --- Car tracking ---
  private updateCarTracking(dt: number): void {
    this.mouseIdleTimer += dt;

    if (this.mouseIdleTimer >= this.MOUSE_IDLE_THRESHOLD && this.hoveredCell) {
      const carAtHover = this.cars.getCarAt(this.hoveredCell.x, this.hoveredCell.y);
      if (carAtHover && this.trackedCarId === null) {
        const carId = this.cars.getCarIdAt(this.hoveredCell.x, this.hoveredCell.y);
        if (carId !== null) {
          this.trackedCarId = carId;
        }
      }
    }

    if (this.mouseIdleTimer < this.MOUSE_IDLE_THRESHOLD && this.trackedCarId !== null) {
      const trackedPos = this.cars.getCarPositionById(this.trackedCarId);
      if (trackedPos && this.hoveredCell) {
        const dist = Math.abs(trackedPos.gridX - this.hoveredCell.x) +
                     Math.abs(trackedPos.gridY - this.hoveredCell.y);
        if (dist > 3) {
          this.trackedCarId = null;
        }
      } else if (!trackedPos) {
        this.trackedCarId = null;
      }
    }
  }

  // --- Debug terrain ---
  private generateDebugTerrain(): void {
    const cols = this.grid.getCols();
    const rows = this.grid.getRows();

    const midY = Math.floor(rows / 2);
    for (let x = 2; x < cols - 2; x++) {
      for (let dy = -1; dy <= 1; dy++) {
        this.grid.setCellState(x, midY + dy, CellState.CURED);
      }
    }

    const midX = Math.floor(cols / 2);
    for (let y = 2; y < rows - 2; y++) {
      for (let dx = -1; dx <= 1; dx++) {
        this.grid.setCellState(midX + dx, y, CellState.CURED);
      }
    }

    for (let x = 5; x < midX - 3; x++) {
      this.grid.setCellState(x, Math.floor(rows / 3), CellState.CURED);
      this.grid.setCellState(x, Math.floor(rows * 2 / 3), CellState.CURED);
    }
    for (let x = midX + 4; x < cols - 5; x++) {
      this.grid.setCellState(x, Math.floor(rows / 3), CellState.CURED);
      this.grid.setCellState(x, Math.floor(rows * 2 / 3), CellState.CURED);
    }
    for (let y = 3; y < midY - 2; y++) {
      this.grid.setCellState(Math.floor(cols / 4), y, CellState.CURED);
      this.grid.setCellState(Math.floor(cols * 3 / 4), y, CellState.CURED);
    }
    for (let y = midY + 3; y < rows - 3; y++) {
      this.grid.setCellState(Math.floor(cols / 4), y, CellState.CURED);
      this.grid.setCellState(Math.floor(cols * 3 / 4), y, CellState.CURED);
    }

    for (let x = midX - 5; x <= midX + 5; x++) {
      for (let y = midY - 5; y <= midY + 5; y++) {
        const cell = this.grid.getCell(x, y);
        if (cell && cell.state === CellState.CURED) {
          if ((x + y) % 3 === 0) {
            this.grid.setCellState(x, y, CellState.PAINTED);
          }
        }
      }
    }

    this.precisionUnlocked = true;
    this.cellsChanged = this.PRECISION_UNLOCK;
    const precisionTool = this.toolDefinitions.find(t => t.id === ParkingtonTool.PRECISION);
    if (precisionTool) {
      precisionTool.available = true;
    }

    console.log('Debug terrain generated: road network with highways, precision tool unlocked');
  }

  // --- Update ---
  update(dt: number): void {
    // The polity first, and *before* the tick, which is where every other input
    // lands: a command is stamped for the tick it will be drained at the head of
    // (ADR-004), so a governor that decided after the tick would be deciding
    // against a district one tick older than the record it read. It is one
    // Chronicle-length comparison in the ordinary case.
    this.seat.observe();

    // Then the simulation, then the client's own business. Nothing below this line
    // may write simulation state: autosave and hover tracking are effects of
    // being watched, and being watched does not change the totals.
    const outcomes = this.sim.update(dt);
    if (outcomes.length > 0) {
      this.recordConstruction(outcomes);
    }
    this.reportMercySpending(dt);
    this.watchForSettlement();
    this.seasonReportCard.update(dt);

    // Snap-back animation
    this.touchZoomHandler.updateSnapBack(this.stateView);
    this.portalPulse += dt * 0.003;

    this.updateCarTracking(dt);

    // Autosave. Outside the sim deliberately (`IMPLEMENTATION-GOVERNORS.md` §5
    // item 4): a headless settlement run must not touch localStorage, and a
    // save must not be able to perturb a tick.
    this.autosaveTimer += dt;
    if (this.autosaveTimer >= this.AUTOSAVE_INTERVAL) {
      this.autosaveTimer = 0;
      this.saveGame(true);
    }
  }

  // --- Render ---
  render(ctx: CanvasRenderingContext2D): void {
    antSpriteCache.initialize();

    // Background
    ctx.fillStyle = '#e8f4f4';
    ctx.fillRect(0, 0, this.width, this.height);

    // Terrain
    if (!this.skipTerrain) {
      this.terrainRenderer.render(ctx, this.camera, this.grid);
    }

    const state = this.stateView;
    const mapCtx = { camera: this.camera, grid: this.grid, cars: this.cars, houses: this.houses, pheromones: this.pheromones, state };

    if (!this.touchZoomHandler.isTransforming) {
      // After the terrain and before the bushes: the marks lie on the ground, and a
      // sapling that has come in is drawn by the ordinary vegetation pass over the
      // top of nothing, because `MercyManager` has stopped listing it.
      this.mercyRenderer.render(ctx, this.camera, this.sim.mercy);
      // After the works, and before the depth-sorted pass: an interchange is a
      // road marking, so it goes under everything that stands on the road.
      this.freightRenderer.render(ctx, this.camera, this.sim.freight);

      if (this.showPheromones) {
        this.mapFeatureRenderer.renderPheromones(ctx, mapCtx);
      }
      if (!this.skipFoodSources) {
        this.foodSourceRenderer.render(ctx, this.camera, this.foodSources.getAllSources());
      }
    }

    // Portals (always render as landmarks)
    this.mapFeatureRenderer.renderPortal(ctx, mapCtx);

    if (!this.touchZoomHandler.isTransforming) {
      // Depth-sorted entities
      this.entityRenderer.renderEntitiesDepthSorted(ctx, {
        camera: this.camera,
        grid: this.grid,
        cars: this.cars,
        houses: this.houses,
        power: this.sim.power,
        water: this.sim.water,
        facilities: this.sim.facilities,
        surfaceAnts: () => this.surfaceAntController.getAnts(),
        skipVegetation: this.skipVegetation,
        skipHouses: this.skipHouses,
        skipCars: this.skipCars,
        skipAnts: this.skipAnts,
      });

      // Car trip path + markers
      this.mapFeatureRenderer.renderCarTripPath(ctx, mapCtx);
      this.mapFeatureRenderer.renderTrackedCarMarkers(ctx, mapCtx);
    }

    // Hover highlight
    this.highlightRenderer.renderHoverHighlight(ctx, {
      camera: this.camera,
      grid: this.grid,
      cars: this.cars,
      houses: this.houses,
      facilities: this.sim.facilities,
      state,
      surfaceAnts: () => this.surfaceAntController.getAnts(),
      getBrushSize: () => this.getBrushSize(),
    });

    // HTML UI update (throttled)
    this.htmlUIRenderer.updateHTMLUI(state, (phase) => this.canvasUIRenderer.getPhaseLabel(phase));

    // Save slot picker (canvas popup)
    if (this.showSaveSlots) {
      const panelWidth = 210;
      const panelX = this.width - panelWidth - 20;
      const saves = this.getSaveSlots();
      if (saves.length > 0) {
        this.canvasUIRenderer.renderSaveSlotPicker(ctx, {
          state,
          isPointInRect,
          formatTimeAgo: (ts) => this.saveLoadSystem.formatTimeAgo(ts),
        }, panelX, 80, panelWidth, saves);
      }
    }

    // Selection pane
    if (this.selectedEntity && this.currentTool === ParkingtonTool.SELECT) {
      const result = this.highlightRenderer.renderSelectionPane(ctx, {
        camera: this.camera,
        grid: this.grid,
        cars: this.cars,
        houses: this.houses,
        facilities: this.sim.facilities,
        state,
        surfaceAnts: () => this.surfaceAntController.getAnts(),
        getBrushSize: () => this.getBrushSize(),
      });
      this.selectionPaneCloseArea = result.closeArea;
      if (!result.entityExists) {
        this.selectedEntity = null;
      }
    }
  }

  // --- Resize ---
  resize(dimensions: Dimensions): void {
    super.resize(dimensions);
    // Only the camera. A new window is not news the district needs: the
    // entrances are cells, and a cell does not move when a window does.
    this.camera.centerInViewport(
      this.width,
      this.height,
      this.grid.getCols(),
      this.grid.getRows(),
    );
  }

  /**
   * The cell under a screen point, or null if the point is off the lot.
   *
   * The camera projects and the grid bounds, and this is the one place the two
   * meet. `TerrainGrid.screenToGrid` used to do both, which is how a bounds
   * check ended up on the far side of a camera (ADR-009).
   */
  private screenToCell(screenX: number, screenY: number): { x: number; y: number } | null {
    const cell = this.camera.screenToGrid(screenX, screenY);
    return this.grid.getCell(cell.x, cell.y) ? cell : null;
  }

  // --- Public API (unchanged) ---
  getGrid(): TerrainGrid { return this.grid; }
  getConstructionManager(): ConstructionManager { return this.construction; }
  getCarManager(): CarManager { return this.cars; }
  getHouseManager(): HouseManager { return this.houses; }
  getSurfaceAntCount(): number { return this.surfaceAntController.getAntCount(); }
  getSurfaceAntCountForColony(colonyIndex: number): number { return this.surfaceAntController.getAntCountForColony(colonyIndex); }
  clearSurfaceAntsForColony(colonyIndex: number): void { this.surfaceAntController.clearAntsForColony(colonyIndex); }
  clearAllSurfaceAnts(): void { this.surfaceAntController.clearAllAnts(); }
  getPortalCells(): Array<{ x: number; y: number }> { return [...this.portalCells]; }
  getRecentNews(count: number): ParkingtonNewsItem[] { return this.houses.getRecentNews(count); }
  setInputBlocked(blocked: boolean): void { this.inputBlocked = blocked; }

  /**
   * Tell this layer which colonies sit beneath it, for the save path.
   *
   * A getter rather than an array, because `Game` owns the colonies and may
   * restart one; the save must read the list at the moment it is taken.
   */
  setColonies(getColonies: () => readonly SnapshotableColony[]): void {
    this.getColonies = getColonies;
  }

  updateCarConfig(partial: Partial<CarManagerConfig>): void { this.sim.updateCarConfig(partial); }
  updateHouseConfig(partial: Partial<HouseManagerConfig>): void { this.sim.updateHouseConfig(partial); }
  updateEconomyConfig(partial: Partial<EconomyConfig>): void { this.sim.updateEconomyConfig(partial); }
  updatePowerConfig(partial: Partial<PowerConfig>): void { this.sim.updatePowerConfig(partial); }
  updateWaterConfig(partial: Partial<WaterConfig>): void { this.sim.updateWaterConfig(partial); }
  updateFacilityConfig(partial: Partial<FacilityConfig>): void { this.sim.updateFacilityConfig(partial); }
  updateLabourConfig(partial: Partial<LabourConfig>): void { this.sim.updateLabourConfig(partial); }
  updateWelfareConfig(partial: Partial<WelfareConfig>): void { this.sim.updateWelfareConfig(partial); }
  updateMercyConfig(partial: Partial<MercyConfig>): void { this.sim.updateMercyConfig(partial); }
  updateFiscalConfig(partial: Partial<FiscalConfig>): void { this.sim.updateFiscalConfig(partial); }
  updateMarketConfig(partial: Partial<MarketConfig>): void { this.sim.updateMarketConfig(partial); }
  updateFreightConfig(partial: Partial<FreightConfig>): void { this.sim.updateFreightConfig(partial); }
  /**
   * Edit the polity's brief.
   *
   * The one config forwarder on this class that does not reach the simulation: a
   * brief is not a world parameter, it is somebody's instructions, and the district
   * neither holds it nor is hashed with it. Which is also why it is not in the save
   * — a loaded district resumes under whatever brief the governor in the chair is
   * playing now, and the consequences of the old one came back with the snapshot.
   */
  updatePolityBrief(partial: Partial<GovernorBrief>): void {
    this.resident.setBrief(partial);
  }
  updateFoodConfig(partial: Partial<FoodSourceConfig>): void { this.sim.updateFoodConfig(partial); }
  updatePheromoneConfig(partial: Partial<PheromoneGridConfig>): void { this.sim.updatePheromoneConfig(partial); }
  updateSurfaceForagingConfig(colonyIndex: number, partial: Record<string, number>): void {
    this.sim.updateSurfaceForagingConfig(colonyIndex, partial);
  }

  /** Apply computed trait parameters. These are simulation inputs. */
  applyTraitParams(params: SurfaceTraitParams): void {
    this.sim.applyTraitParams(params);
  }

  /** The simulation this layer draws, for tests and for the settlement path. */
  getSim(): SurfaceSim {
    return this.sim;
  }
}

// Re-export the old ParkingLot name for backwards compatibility
export { ParkingtonLayer as ParkingLot };
